import {Injectable} from '@angular/core';
import * as _ from "lodash";

import {DatabaseService} from "./database.service";
import {SubtitlesService} from "./subtitles.service";
import {
    ContainerConnection, ContainerOrder,
    RawConnectionData,
    SingleConnection,
    SystemContainerData,
    SystemContainerView,
    SystemContainerViewWithId, SystemZoneOrder,
    ZonesData,
    ZonesTileData,
    ZoneTileTypeId, ZoneViewData
} from "../models";

@Injectable({
    providedIn: 'root'
})
export class ZonesSystemService {
    containers: SystemContainerViewWithId[] | null[] = [null];  // null means one container for all tiles (for non-system zones controller)
    systemTiles: ZonesTileData[] = [];
    private tempContainers: SystemContainerViewWithId[] = [];

    constructor(
        private database: DatabaseService,
        private subtitles: SubtitlesService,
    ) {
    }

    private subtitle(id: number): string {
        return this.subtitles.setSubtitle(id);
    }

    private setSystemTiles(zoneClickedId: ZoneTileTypeId): void {
        let zones = this.database.zones.getValue() as ZonesData;

        if (this.hasZonesSystemData()
            && Object.keys(zones).length !== 0
            && zones.globalSchedules.elements.length === 5
        ) {
            const tilesData = this.database.tiles.getValue();
            if (Array.isArray(tilesData)) {
                this.systemTiles = tilesData
                    .filter((tile) => {
                        return tile.visibility !== false;
                    })
                    .filter((tile) => {
                        return tile.type !== 50;
                    })
                    .filter((tile) => {
                        return tile.type !== 61;
                    })
                    .filter((tile) => {
                        return !(tile.type === 1 && tile.params.workingStatus === false);
                    })
                    .map((tile) => {
                        tile.isSystemTile = true;
                        return tile;
                    });
            }

            zones.system.additionalContacts
                .filter((contact) => {
                    return contact.workType !== -1;
                })
                .forEach((contact) => {
                    contact.isSystemTile = true;
                    contact.workTypeSubtitle = this.setWorkTypeSubtitle(contact.workType);
                    this.systemTiles.push(contact);
                });

            this.attachTilesToContainers();
            this.attachAdditionalContactsToContainers();

            if (this.containers[0] === null || zoneClickedId) {
                this.containers = _.cloneDeep(this.tempContainers);
            }
        } else {
            this.systemTiles = [];
        }
    }

    private attachAdditionalContactsToContainers(): void {
        const additionalContacts = this.systemTiles.filter(
            tile => tile.type === 'additionalContact'
        );

        additionalContacts.forEach(contact => {
            const tileIndex = this.systemTiles.findIndex(function (tile) {
                return tile.id === contact.id;
            });
            this.moveSystemTile(contact.parentId, tileIndex);
        });
    }

    private moveSystemTile(containerId: number, tileIndex: number): void {
        const containerIndex = this.tempContainers.findIndex(
            container => container.containerId === containerId
        );
        if (containerIndex === -1 || tileIndex === -1) return;

        const tile = this.systemTiles[tileIndex];
        this.tempContainers[containerIndex].data.tiles.push(tile);
        this.systemTiles.splice(tileIndex, 1);
    }

    private attachTilesToContainers() {
        const tilesData = this.database.tiles.getValue();
        if (!Array.isArray(tilesData)) return;

        const tileContainers = tilesData.filter((tile) => tile.type === 61);
        if (tileContainers.length === 0) return;

        this.tempContainers.forEach((container) => {
            container.data.tiles = [];
        });

        tileContainers.forEach((tileContainer) => {
            const tileIndex = this.systemTiles.findIndex((tile) => {
                return tile.parentId === tileContainer.id;
            });

            this.moveSystemTile(tileContainer.params.containerId, tileIndex);
        });
    }

    setContainers(id?: ZoneTileTypeId): void {
        this.tempContainers = [];
        let containers = [null]

        const zonesData = this.database.zones.getValue() as ZonesData;
        if(zonesData.system && zonesData.system.containers) {
            containers = zonesData.system.containers.map((container, idx) => {
                let containerData: any = {};
                containerData.containerId = container.containerId;
                containerData.data = _.cloneDeep(container);
                containerData.data.zones = this.getContainerZones(container.containerId).sort(this.sortByZoneIdAsc);
                containerData.data.parameters = this.getContainerParameters(container.containerId);
                containerData.data.connections = this.getContainerConnections(containerData.data as SystemContainerView);
                containerData.data.tiles = [];
                containerData = this.setContainerOrder(containerData as SystemContainerViewWithId, idx);
                if ('system' in this.database.zones) {
                    containerData.data.duringChange = zonesData.system.duringChange.includes(container.containerId);
                }

                return containerData as SystemContainerViewWithId;
            }).sort(this.sortByOrderAsc);
        }

        if (id) {
            containers.forEach((container, containerIndex) => {
                if (this.containers[containerIndex]) {
                    containers[containerIndex].data.expanded = this.containers[containerIndex].data.expanded;
                }
            });
        }

        this.tempContainers = containers;
        this.setSystemTiles(id);
        this.containers = _.cloneDeep(this.tempContainers);
        this.tempContainers = [];
    }

    setContainerExpand(containerId: number, isOpen: boolean): void {
        const containerIndex = this.containers.findIndex(container => {
            return container.containerId === containerId
        })
        this.containers[containerIndex].data.expanded = isOpen;
    }

    setWorkTypeSubtitle(status: number): string {
        switch (status) {
            case 0:
                return this.subtitle(3517);
            case 1:
                return this.subtitle(3516);
            case 2:
                return this.subtitle(293);
            case 3:
                return this.subtitle(3731);
            case 4:
                return this.subtitle(8434);
            default:
                return '';
        }
    }

    getContainers() {
        return this.containers;
    }

    private getContainerZones(containerId: number): ZoneViewData[] {
        const zonesData = this.database.zones.getValue() as ZonesData;
        return zonesData.elements.filter((zone) => {
            return zone.zone.parentId === containerId;
        });
    }

    private getContainerParameters(containerId: number): SystemContainerData {
        const zonesData = this.database.zones.getValue() as ZonesData;
        return zonesData.system.containersData.find((params) => {
            return params.parentId === containerId
        });
    }

    private setConnectionArray(connections: RawConnectionData): SingleConnection[] {
        const array = [];
        for (const key in connections) {
            array.push({key: key, value: connections[key]})
        }
        return array;
    }

    private getContainerConnections(container: SystemContainerView) {
        const pumpsConnection = this.setConnectionArray(container.parameters.pumpsConnection);
        const freeContactConnection = this.setConnectionArray(container.parameters.freeContactConnection);
        const hcConnection = this.setConnectionArray(container.parameters.hcConnection);

        let ecoInputConnection: SingleConnection[] = [];
        if (container.parameters.ecoInputConnection) {
            ecoInputConnection = this.setConnectionArray(container.parameters.ecoInputConnection);
        }

        const connections: ContainerConnection[] = [];
        for (let i = 0; i < pumpsConnection.length; i += 8) {
            const connectionsChunk: ContainerConnection = {
                pumpsConnection: pumpsConnection.slice(i, i + 8),
                freeContactConnection: freeContactConnection.slice(i, i + 8),
                hcConnection: hcConnection.slice(i, i + 8),
            }
            if (Object.keys(ecoInputConnection).length) {
                connectionsChunk.ecoInputConnection = ecoInputConnection.slice(i, i + 8);
            }
            connections.push(connectionsChunk);
        }
        return connections;
    }

    private getContainerOrder(containerId: number): ContainerOrder {
        const zonesData = this.database.zones.getValue() as ZonesData;
        return zonesData.system.options.containersOrder
            .find(function (order) {
                return order.id === containerId;
            });

    }

    getFullName(zoneId: number): string {
        const zonesData = this.database.zones.getValue();
        if (!this.database.isZonesDataType(zonesData) || !zonesData.system) return '';

        const zone = zonesData.elements.find(zone => zone.zone.id === zoneId);
        if (!zone) return '';

        const container = zonesData.system.containers.find(
            container => container.containerId === zone.zone.parentId
        );

        return container
            ? `${container.containerName}: ${zone.description.name}`
            : '';
    }

    getZonesOrder(zoneId: number): SystemZoneOrder {
        const zoneOrder: SystemZoneOrder = {
            containerOrder: -1,
            zoneIndex: -1,
            zoneId,
        };

        const zonesData = this.database.zones.getValue();
        if (!this.database.isZonesDataType(zonesData) || !zonesData.system) return zoneOrder;

        const zone = zonesData.elements.find(zone => zone.zone.id === zoneId);
        if (!zone) return zoneOrder;

        const containerIndex = zonesData.system.containers.findIndex(
            container => container.containerId === zone.zone.parentId
        );
        if (containerIndex === -1) return zoneOrder;

        const configuredOrder = zonesData.system.options?.containersOrder?.find(
            order => order.id === zone.zone.parentId
        );

        zoneOrder.containerOrder = configuredOrder?.orderId ?? containerIndex + 1;
        zoneOrder.zoneIndex = zone.zone.index;

        return zoneOrder;
    }

    private getSystemTiles(): ZonesTileData[] { // unused method
        return this.systemTiles;
    }

    private sortByOrderAsc(a: SystemContainerViewWithId, b: SystemContainerViewWithId): number {
        return a.data.order - b.data.order;
    }

    private sortByZoneIdAsc(a: ZoneViewData, b: ZoneViewData): number {
        return a.zone.id - b.zone.id;
    }

    hasZonesData(): boolean {
        const hasZonesData = this.database.hasZonesData();
        if (!hasZonesData) this.containers = [null];
        return hasZonesData;
    }

    hasZonesSystemData() {
        // true if first container is any object, false if first container is null
        return Boolean(this.containers[0] || this.tempContainers[0]);
    }

    updateZonesInSystemController(){
        const newZonesData = this.database.zones.getValue() as ZonesData;

        newZonesData.elements.forEach((updatedZone) => {
            let isUpdated = false;
            const updatedZoneId = updatedZone.zone.id;
            const containerId = updatedZone.zone.parentId;

            this.containers.forEach((container) =>{
                if (container.containerId === containerId) {
                    container.data.zones.forEach((zone, zoneIndex) =>{
                        if (zone.zone.id === updatedZoneId) {
                            isUpdated = true;
                            container.data.zones[zoneIndex] = _.cloneDeep(updatedZone);
                        }
                    })
                }
            });
            if(!isUpdated){
                const containerIndex = this.containers.findIndex((container) => {
                    return container.containerId === containerId;
                })
                if (containerIndex >= 0) {
                    this.containers[containerIndex].data.zones.push(updatedZone);
                    this.containers[containerIndex].data.zones.sort(this.sortByZoneIdAsc);
                }
            }
            isUpdated = false;
        });
    }

    updateTilesInSystemController(receivedTiles:  ZonesTileData[]) {
        const filteredReceivedTiles = receivedTiles
            .filter(tile => tile.type !== 50)
            .filter(tile => tile.type !== 61) // type 61 is never updated after init
            .map(tile => {
                tile.isSystemTile = true;
                return tile;
            });

        filteredReceivedTiles.forEach(updatedTile => {
            if (updatedTile.type === 24) {
                this.updateValveAttachedToContainer(updatedTile);
            } else {
                this.updateNonAttachedTiles(updatedTile);
            }
        });
    }

    private updateValveAttachedToContainer(updatedTile: ZonesTileData): void {
        let isUpdated = false;
        const updatedTileId = updatedTile.id;

        this.containers.forEach(container =>{
            const tileIndex = container.data.tiles.findIndex(
                tile => tile.id === updatedTileId
            );

            if (tileIndex !== -1) {
                const updatedData = _.cloneDeep(container.data);

                // if visibility is false the valve was unregistered
                if (!updatedTile.visibility) {
                    updatedData.tiles.splice(tileIndex, 1);
                } else {
                    updatedData.tiles[tileIndex] = updatedTile;
                }
                container.data = updatedData;
                isUpdated = true;
            }
        });
        if (!isUpdated){
            const tilesData = this.database.tiles.getValue();

            if (Array.isArray(tilesData)) {
                const tileContainer = tilesData.find(
                    tile => tile.type === 61 && tile.id === updatedTile.parentId
                );
                const containerId = tileContainer.params.containerId;
                const containerIndex = this.containers.findIndex(
                    container => container.containerId === containerId
                );

                if (containerIndex !== -1 && updatedTile.visibility) {
                    this.containers[containerIndex].data.tiles.push(updatedTile);
                }
            }
        }
    }

    private updateNonAttachedTiles(updatedTile: ZonesTileData): void {
        let isUpdated = false;
        const updatedTileId = updatedTile.id;
        this.systemTiles.forEach((tile, tileIndex) => {
            if (tile.id === updatedTileId) {
                if ( !updatedTile.visibility || (updatedTile.type === 1 && !updatedTile.params.workingStatus )) {
                    this.systemTiles.splice(tileIndex, 1);
                } else {
                    this.systemTiles[tileIndex] = updatedTile;
                }
                isUpdated = true;
            }
        });
        if(!isUpdated) {
            this.systemTiles.push(updatedTile);
        }
    }

    updateSystemDataInSystemController(){
        const newData = this.database.zones.getValue() as ZonesData;

        this.containers.forEach((container, index) =>{
            const newContainer = newData.system.containers.find((updatedContainer) => {
                return updatedContainer.containerId === container.containerId;
            });
            let containerData: any = {};
            containerData.data = _.cloneDeep(newContainer);
            containerData.containerId = newContainer.containerId;
            containerData.data.zones = _.cloneDeep(container.data.zones);
            containerData.data.tiles = _.cloneDeep(container.data.tiles);
            containerData.data.containerId = newContainer.containerId;
            containerData.data.parameters = this.getContainerParameters(newContainer.containerId);
            containerData.data.connections = this.getContainerConnections(containerData.data);

            containerData = this.setContainerOrder(containerData, index);
            containerData.data.expanded = this.containers[index].data.expanded;
            this.containers[index].data = containerData.data;
        });

        const updatedContacts = newData.system.additionalContacts
            .filter((contact) =>{ return contact.workType !== -1; })
            .map((contact) => {
                contact.isSystemTile = true;
                contact.workTypeSubtitle = this.setWorkTypeSubtitle(contact.workType);
                return contact;
            });

        // remove old additional contacts before update
        this.containers.forEach((container) =>{
            container.data.tiles = container.data.tiles.filter((tile) =>{
                return tile.type !== 'additionalContact';
            });
        });

        updatedContacts.forEach((updatedContact) => {
            const containerIndex = this.containers.findIndex((container) => {
                return container.containerId === updatedContact.parentId;
            })
            this.containers[containerIndex].data.tiles.push(updatedContact);
        });
    }

    private setContainerOrder(containerData: SystemContainerViewWithId, idx: number): SystemContainerViewWithId {
        const zonesData = this.database.zones.getValue() as ZonesData;
        if (zonesData.system.options &&
            zonesData.system.options.hasOwnProperty('containersOrder')
            && zonesData.system.options.containersOrder.length > 0
        ) {
            const order = this.getContainerOrder(containerData.containerId);
            containerData.data.order = order.orderId;
            containerData.data.expanded = order.expanded;
        } else {
            containerData.data.order = idx + 1;
            containerData.data.expanded = true;
        }
        return containerData;
    }

    resetZonesSystemService() {
        this.containers = [null];
        this.systemTiles = [];
        this.tempContainers = [];
    }
}
